import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  MenuItem,
  InputAdornment,
  Checkbox,
  DialogActions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { saveAs } from "file-saver";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CalculateIcon from "@mui/icons-material/Calculate";
import Accordion from "react-bootstrap/Accordion";
import {
  MaterialReactTable,
  MRT_Row,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
} from "material-react-table";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";

import {
  FormData,
  FilterState,
  SelectOption,
  LastImRequest,
} from "./initialMargin.types";
import * as imActions from "./initialMargin.actions";
import {
  IM_RESULT_HEADERS,
  IM_RESULT_COLUMN_SIZES,
  LAST_DATE_FORMAT,
  TILL_DATE_FORMAT,
  getBufferedRows,
} from "./initialMargin.actions";
import { Row, Container } from "reactstrap";
import { getLastBusinessDay } from "./InitialMarginSummary";

/* ---------- UI constants ---------- */

const EXCHANGE_CODES = {
  I: "ICE Energy",
  L: "Financials",
  O: "Equity Options",
  X: "Commodities",
  F: "FX",
  G: "ICE Clear Singapore",
  N: "ICE Clear U.S.",
  T: "ICE Clear Netherlands",
};

const CONTRACT_TYPES = {
  F: "Futures",
  C: "Call Options",
  P: "Put Options",
  M: "Monthly Contracts",
  D: "Daily Contracts",
  N: "NDF (OTC FX Contracts)",
};

const REGIMES = ["", "DCO", "RCH"];
const CUSTOMER_TYPES = ["", "H", "S", "M"];
const OUTRIGHT_MARGIN_OPTIONS = ["", "Y", "N"];
const CSV_COLUMNS = [
  "PositionFlag",
  "Account",
  "ExchangeCode",
  "ContractCode",
  "ContractType",
  "ExpiryYYYYMMDD",
  "Strike",
  "NetPosition",
  "OutrightMargin",
  "Regime",
  "CustomerType",
];

const FIELD_LABELS: Record<string, string> = {
  PositionFlag: "Position/Trade Flag",
  Account: "Account Name/Identifier",
  ExchangeCode: "Exchange Code",
  ContractCode: "Exchange Contract Code",
  ContractType: "Contract Type",
  ExpiryYYYYMMDD: "Expiry (YYYYMMDD)",
  Strike: "Strike Price",
  NetPosition: "Net Position",
  Regime: "Regime",
  CustomerType: "Customer Type",
  OutrightMargin: "Outright Margin",
  Strategy: "Strategy",
};

// Always required. Strike is also required, but only for options (see isRequired below).
const REQUIRED_FIELDS = [
  "Account",
  "ExchangeCode",
  "ContractCode",
  "ContractType",
  "ExpiryYYYYMMDD",
  "NetPosition",
];

const makeEmptyRow = () => ({
  PositionFlag: "P",
  Account: "",
  ExchangeCode: "I",
  ContractCode: "",
  ContractType: "F",
  ExpiryYYYYMMDD: "",
  Strike: "",
  NetPosition: "",
  Regime: "",
  CustomerType: "",
  OutrightMargin: "",
  Strategy: "",
});

const makeEmptyFilters = (): FilterState => ({
  exchange: "",
  book: [],
  strategy: [],
  clearer: [],
  companyType: "",
});

const TABLE_MAX_HEIGHT = "350px";

// red border for an invalid outlined input (placed after hover/focus rules so it wins)
const errorBorderSx = {
  "&.Mui-error fieldset": { borderColor: "#d32f2f" },
  "&.Mui-error:hover fieldset": { borderColor: "#d32f2f" },
  "&.Mui-error.Mui-focused fieldset": { borderColor: "#d32f2f" },
};

// Filter dropdowns show the name but hold the backend id, which is what gets sent as the param.
// Falls back to the name when an option has no id.
const toIdOptions = (opts: SelectOption[]): SelectOption[] =>
  opts
    .filter((o) => o.value)
    .map((o) => ({ value: o.id ?? o.value, label: o.label }));

// same container behaviour for both tables; no cap in fullscreen
const tableContainerProps = ({ table }: any) => ({
  sx: {
    backgroundColor: "#fff",
    maxHeight: table.getState().isFullScreen ? "none" : TABLE_MAX_HEIGHT,
  },
  className: "table-container-wrapper__new",
});

/* ---------- validation (field -> message) ---------- */

const validateFields = (row: any): Record<string, string> => {
  const e: Record<string, string> = {};

  // A: PositionFlag must be 'P'
  if (String(row.PositionFlag ?? "").trim().toUpperCase() !== "P") {
    e.PositionFlag = "PositionFlag must be 'P'.";
  }

  // B: Account non-empty
  if (!String(row.Account ?? "").trim()) {
    e.Account = "Account name is required.";
  }

  // C: Exchange code valid
  const validExchanges = ["I", "L", "O", "X", "F", "G", "N", "T"];
  const exch = String(row.ExchangeCode ?? "").trim().toUpperCase();
  if (!exch) {
    e.ExchangeCode = "Exchange code is required.";
  } else if (!validExchanges.includes(exch)) {
    e.ExchangeCode = `ExchangeCode '${row.ExchangeCode}' is invalid.`;
  }

  // D: ContractCode non-empty
  if (!String(row.ContractCode ?? "").trim()) {
    e.ContractCode = "ContractCode is required.";
  }

  // E: Contract type valid
  const validTypes = ["F", "C", "P", "M", "D", "N"];
  const ct = String(row.ContractType ?? "").trim().toUpperCase();
  if (!ct) {
    e.ContractType = "Contract type is required.";
  } else if (!validTypes.includes(ct)) {
    e.ContractType = `ContractType '${row.ContractType}' is invalid.`;
  }

  // F: Expiry format YYYYMMDD (or YYYYMM00)
  const expiry = String(row.ExpiryYYYYMMDD || "").trim();
  if (!expiry) {
    e.ExpiryYYYYMMDD = "ExpiryYYYYMMDD is required (YYYYMMDD or YYYYMM00).";
  } else if (!/^\d{8}$/.test(expiry)) {
    e.ExpiryYYYYMMDD = "Expiry must be 8 digits (YYYYMMDD).";
  } else {
    const year = Number(expiry.slice(0, 4));
    const month = Number(expiry.slice(4, 6));
    const day = Number(expiry.slice(6, 8));
    if (month < 1 || month > 12) {
      e.ExpiryYYYYMMDD = "Expiry month must be between 01 and 12.";
    } else if (day !== 0) {
      const date = new Date(year, month - 1, day);
      if (isNaN(date.getTime()) || date.getMonth() + 1 !== month) {
        e.ExpiryYYYYMMDD = "Expiry day is invalid for given month/year.";
      }
    }
  }

  // G: Strike validation
  const strike = String(row.Strike || "").trim();
  if (["C", "P"].includes(ct)) {
    if (!strike) {
      e.Strike = "Strike is required for options.";
    } else if (isNaN(Number(strike))) {
      e.Strike = "Strike must be numeric for options.";
    }
  } else if (strike && isNaN(Number(strike))) {
    e.Strike = "Strike must be numeric or blank for non-options.";
  }

  // H: NetPosition numeric
  const np = String(row.NetPosition ?? "").trim();
  if (!np) {
    e.NetPosition = "NetPosition is required.";
  } else if (isNaN(Number(np))) {
    e.NetPosition = "NetPosition must be numeric (positive or negative).";
  }

  // I: Regime optional but must be valid
  const validRegimes = ["", "DCO", "RCH"];
  if (
    row.Regime &&
    !validRegimes.includes(String(row.Regime).trim().toUpperCase())
  ) {
    e.Regime = "Regime must be DCO or RCH if specified.";
  }

  // J: CustomerType optional but must be valid
  const validCust = ["", "H", "S", "M"];
  if (
    row.CustomerType &&
    !validCust.includes(String(row.CustomerType).trim().toUpperCase())
  ) {
    e.CustomerType = "CustomerType must be H, S or M if specified.";
  }

  return e;
};

// flat list of messages (used by CSV upload and Validate All)
const validateRow = (row: any): string[] => Object.values(validateFields(row));

const InitialMargin: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormData>(makeEmptyRow());
  // invalid fields of the add/edit form (red border only), keyed by field name
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editIndex, setEditIndex] = useState<number | number[] | null>(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState<FilterState>(makeEmptyFilters());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [bulkDeleteRows, setBulkDeleteRows] = useState<any[]>([]);

  const [bookOptions, setBookOptions] = useState<SelectOption[]>([]);
  const [custBookOptions, setCustBookOptions] = useState<SelectOption[]>([]);
  const [clearerOptions, setClearerOptions] = useState<SelectOption[]>([]);
  const [strategyOptions, setStrategyOptions] = useState<SelectOption[]>([]);
  const [allBookOptions, setAllBookOptions] = useState<SelectOption[]>([]);

  const [isLoadingBooks, setIsLoadingBooks] = useState(false);
  const [isLoadingCustBooks, setIsLoadingCustBooks] = useState(false);
  const [isLoadingClearers, setIsLoadingClearers] = useState(false);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const [isCalculating, setIsCalculating] = useState(false);
  const [imResult, setImResult] = useState<any | null>(null);
  // true when run-im returned an earlier result instead of calculating again
  const [imReused, setImReused] = useState(false);
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  // what the last Calculate IM sent, so Rerun recalculates exactly the same positions and dates
  const lastImRequestRef = useRef<LastImRequest | null>(null);
  // stops the run-im polling loop once the page is left
  const isMountedRef = useRef(true);
  // the result section sits below the positions table; scrolled to when a job finishes
  const imResultRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [showImTable, setShowImTable] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [, setUploadedFileName] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [showActionsHeader, setShowActionsHeader] = useState(false);

  // Business date (sent as YYYYMMDD)
  const [lastDate, setLastDate] = useState<dayjs.Dayjs | null>(() => getLastBusinessDay());
  // inline error for the business date popup
  const [dateError, setDateError] = useState("");
  // "Trades till" filter for fetching positions (sent as till_date=YYYY-MM-DD)
  const [tillDate, setTillDate] = useState<dayjs.Dayjs | null>(null);
  // "Including" box: always sent as include_till_date=true/false; false leaves the selected date's trades out
  const [includeTillDate, setIncludeTillDate] = useState(true);
  // Expiry picker: true when the user wants to pick a specific day instead of a month
  const [expiryDayMode, setExpiryDayMode] = useState(false);

  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  /* ---------- dropdown data ---------- */
  useEffect(() => {
    const load = async <T,>(
      fn: () => Promise<T>,
      set: (v: T) => void,
      setLoading: (b: boolean) => void
    ) => {
      try {
        setLoading(true);
        set(await fn());
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load(
      imActions.fetchBookOptions,
      (o) => {
        setBookOptions(o);
        setAllBookOptions(o);
      },
      setIsLoadingBooks
    );
    load(imActions.fetchClearerOptions, setClearerOptions, setIsLoadingClearers);
    load(imActions.fetchStrategyOptions, setStrategyOptions, setIsLoadingStrategies);
    load(imActions.fetchCounterpartyOptions, setCustBookOptions, setIsLoadingCustBooks);
  }, []);

  useEffect(() => {
    if (filters.companyType === "Counterparty") {
      setBookOptions(custBookOptions);
    } else {
      setBookOptions(allBookOptions);
    }

    // Always reset the selected book when switching company type
    setFilters((prev) => ({ ...prev, book: [] }));
  }, [filters.companyType, custBookOptions, allBookOptions]);

  const filtersConfig: {
    label: string;
    key: keyof FilterState;
    options: SelectOption[]; // value = what goes into filters/params (id), label = what is shown
    multiple?: boolean;
  }[] = [
    {
      label: "Exchange",
      key: "exchange",
      options: ["ICE", "EEX", "NYMEX"].map((v) => ({ value: v, label: v })), // static
    },
    {
      label: "Company Type",
      key: "companyType",
      options: ["Organization", "Counterparty"].map((v) => ({ value: v, label: v })), // static
    },
    {
      label: "Book",
      key: "book",
      options: toIdOptions(bookOptions), // dynamic
      multiple: true,
    },
    {
      label: "Strategy",
      key: "strategy",
      options: toIdOptions(strategyOptions), // dynamic
      multiple: true,
    },
    {
      label: "Clearer",
      key: "clearer",
      options: clearerOptions.filter((o) => o.value), // value = name (sent as-is)
      multiple: true,
    },
  ];

  /* ---------- form validation helpers ---------- */

  // Strike is required only for call/put options
  const isRequired = (field: string) =>
    REQUIRED_FIELDS.includes(field) ||
    (field === "Strike" &&
      ["C", "P"].includes(String(formData.ContractType || "").toUpperCase()));

  const labelFor = (field: string) =>
    `${FIELD_LABELS[field] || field}${isRequired(field) ? " *" : ""}`;

  // spread onto a TextField: red state only, no message under the field
  const errProps = (field: string) => ({
    error: !!formErrors[field],
  });

  /* ---------- API handlers ---------- */
  const openCalculateDialog = () => {
    if (rows.length === 0) {
      toast.error("No trades to calculate IM");
      return;
    }
    setDateError("");
    setDateDialogOpen(true);
  };
  const resetTable = () => {
    setRows([]);
    setRowSelection({});
    setGlobalFilter("");
    setImResult(null);
    setShowImTable(false);
    setShowActionsHeader(false);
    setEditIndex(null);
    setHasFormChanges(false);
    setFormData(makeEmptyRow());
    setFormErrors({});
  };

  // Starts the job and polls it. On rerun the previous result stays on screen until the fresh one arrives.
  const runIm = async (req: LastImRequest, rerun: boolean) => {
    try {
      setIsCalculating(true);
      if (!rerun) {
        setImResult(null);
        setImReused(false);
        setShowImTable(false);
      }

      const { jobId, reused } = await imActions.startImJob(req, rerun);
      const result = await imActions.pollImJob(
        jobId,
        () => isMountedRef.current
      );
      if (!result) return; // page left

      setImResult(result);
      setImReused(reused);
      setShowImTable(true);
      setTimeout(
        () =>
          imResultRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        100
      );
      if (getBufferedRows(result).length === 0) {
        toast.warn("IM job finished but no result rows were found in the response.");
      }
      if (Array.isArray(result?.warnings) && result.warnings.length > 0) {
        toast.warn(result.warnings.map((w: any) => String(w)).join("; "));
      }
      toast.success(
        reused
          ? `IM result reused from an earlier run (${req.rowCount} rows).`
          : rerun
          ? `IM recalculated for ${req.rowCount} rows!`
          : `IM calculation completed for ${req.rowCount} rows!`
      );
      if (!rerun) setRowSelection({});
    } catch (error: any) {
      console.error("Error calculating IM:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to calculate IM"
      );
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCalculateIM = async () => {
    const selectedIdx = Object.keys(rowSelection);
    const selected =
      selectedIdx.length > 0
        ? selectedIdx.map((i) => rows[parseInt(i)]).filter(Boolean)
        : rows;
    if (selected.length === 0) {
      toast.error("No trades to calculate IM");
      return;
    }
    if (!lastDate || !lastDate.isValid()) {
      setDateError("Business date is required");
      return;
    }
    setDateError("");
    setDateDialogOpen(false);

    const blob = imActions.buildPositionsBlob(selected);
    // Download a copy of the exact file sent to run-im
     saveAs(blob, `im_calc_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);

    const req: LastImRequest = {
      file: blob,
      businessDate: lastDate.format(LAST_DATE_FORMAT),
      tillDate:
        tillDate && tillDate.isValid()
          ? tillDate.format(TILL_DATE_FORMAT)
          : null,
      includeTillDate,
      companyType: filters.companyType,
      rowCount: selected.length,
    };
    lastImRequestRef.current = req; // Rerun reuses exactly this
    await runIm(req, false);
  };

  const handleRerunIM = () => {
    const last = lastImRequestRef.current;
    if (!last) return;
    setRerunConfirmOpen(false);
    runIm(last, true);
  };

  const handleFilterApply = async () => {
    setImResult([]);
    try {
      const apiRows = await imActions.fetchPositions(
        filters,
        tillDate,
        includeTillDate
      );
      if (!apiRows) {
        toast.warn("No trades found for either futures or swaps.", {
          position: "top-right",
        });
        return;
      }
      // Merge API data with existing manual rows
      setRows((prevRows) => [...prevRows, ...apiRows]);
      setShowActionsHeader(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data from server.", {
        position: "top-right",
      });
    }
  };

  const handleValidateAll = () => {
    const allErrors: Record<number, string[]> = {};

    rows.forEach((row, index) => {
      const errors = validateRow(row);
      if (errors.length > 0) {
        allErrors[index] = errors;
      }
    });

    if (Object.keys(allErrors).length === 0) {
      toast.success("All rows are valid");
    } else {
      Object.entries(allErrors).forEach(([idx, errs]) => {
        toast.error(`Row ${idx}: ${errs.join("; ")}`);
      });
    }
  };

  /* ---------- form / row handlers ---------- */

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasFormChanges(true);
    // clear this field's error as soon as the user edits it
    // (changing the contract type also re-evaluates whether Strike is required)
    setFormErrors((prev) => {
      if (!prev[field] && !(field === "ContractType" && prev.Strike)) return prev;
      const next = { ...prev };
      delete next[field];
      if (field === "ContractType") delete next.Strike;
      return next;
    });
  };

  const handleAddOrUpdate = () => {
    setImResult([]);

    // Validate single-row add/update; bulk edit only applies the fields the user filled in
    const isBulk = Array.isArray(editIndex);
    if (!isBulk) {
      const errs = validateFields(formData);
      if (Object.keys(errs).length > 0) {
        setFormErrors(errs); // red borders on the invalid fields
        toast.error("Please fill all the highlighted fields", {
          position: "top-right",
          autoClose: 2000,
        });
        return;
      }
    }
    setFormErrors({});

    if (editIndex !== null && !Array.isArray(editIndex)) {
      // Single-edit path
      const updated = [...rows];
      updated[editIndex] = formData;
      setRows(updated);
      setEditIndex(null);
      toast.success("Edited successfully!");
    } else if (Array.isArray(editIndex)) {
      // Bulk-edit path: apply only fields that user entered in formData
      applyBulkUpdate(formData as Partial<FormData>);
    } else {
      // Add new row
      setRows((prev) => [...prev, formData]);
      setShowActionsHeader(true);
      toast.success("Added successfully!");
    }

    setFormData(makeEmptyRow());
    setHasFormChanges(false);
    setExpiryDayMode(false);
  };

  const handleEdit = (index: number) => {
    setExpiryDayMode(false);
    setFormErrors({});
    setFormData(rows[index]);
    setEditIndex(index);
    setHasFormChanges(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBulkEdit = (selectedRows: MRT_Row<any>[]) => {
    if (!selectedRows || selectedRows.length === 0) return;

    // collect numeric indexes
    const indexes = selectedRows.map((r) => r.index);

    // Clear form so user can enter values that will be applied to all selected rows.
    setFormData(makeEmptyRow());
    setFormErrors({});

    // store indexes array to indicate bulk-edit mode
    setEditIndex(indexes);
    setHasFormChanges(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyBulkUpdate = (updatedFields: Partial<FormData>) => {
    if (!Array.isArray(editIndex) || editIndex.length === 0) return;

    const newRows = [...rows];

    // For each selected row index, merge only non-empty fields from updatedFields
    editIndex.forEach((idx) => {
      const original = newRows[idx] || {};
      const merged = { ...original };

      Object.entries(updatedFields).forEach(([key, value]) => {
        // treat empty string as "no change"
        if (value !== "" && value !== undefined && value !== null) {
          merged[key] = value;
        }
      });

      newRows[idx] = merged;
    });

    setRows(newRows);
    setFormData(makeEmptyRow());
    setEditIndex(null);
    setRowSelection({});
    toast.success("Bulk edit applied to selected rows!");
  };

  const handleDelete = (index: number) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };
  const handleBulkDelete = (selectedRows: MRT_Row<any>[]) => {
    if (selectedRows.length === 0) return;
    setBulkDeleteRows(selectedRows);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      const updated = [...rows];
      updated.splice(deleteIndex, 1);
      setRows(updated);
      toast.success("Deleted successfully!");
      setDeleteIndex(null);
    } else if (bulkDeleteRows.length > 0) {
      const updated = rows.filter(
        (row, index) =>
          !bulkDeleteRows.some((selectedRow) => selectedRow.index === index)
      );
      setRows(updated);
      setRowSelection({});
      toast.success("Selected rows deleted successfully!");
      setBulkDeleteRows([]);
    }

    setConfirmOpen(false);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
    setBulkDeleteRows([]);
  };

  const downloadRowsCsv = (data: any[], prefix = "im") => {
    const header = CSV_COLUMNS.map((col) => headerMap[col] || col).join(",");
    const body = data.map((r) => CSV_COLUMNS.map((col) => r[col] ?? "").join(","));
    const csv = [header, ...body].join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${prefix}_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  const handleDownload = () => downloadRowsCsv(rows);
  // Expiry can be a month (YYYYMM00) or a specific day (YYYYMMDD)
  const expiryStr = String(formData.ExpiryYYYYMMDD || "");
  const isDayExpiry =
    formData.ContractType === "D" ||
    expiryDayMode ||
    (/^\d{8}$/.test(expiryStr) && !expiryStr.endsWith("00"));

  const handleExpiryDayToggle = (checked: boolean) => {
    setExpiryDayMode(checked);
    if (!/^\d{8}$/.test(expiryStr)) return;
    // keep the chosen month; switch the day part between 00 (month only) and a real day
    if (checked && expiryStr.endsWith("00")) {
      handleChange("ExpiryYYYYMMDD", `${expiryStr.slice(0, 6)}01`);
    } else if (!checked && !expiryStr.endsWith("00")) {
      handleChange("ExpiryYYYYMMDD", `${expiryStr.slice(0, 6)}00`);
    }
  };

  const handleClearForm = () => {
    setExpiryDayMode(false);
    setFormData(makeEmptyRow());
    setFormErrors({});
    setEditIndex(null);
    setHasFormChanges(false);
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    setUploadedFileName(file.name);

    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      let lines = text.trim().split("\n");

      if (/^position\/trade flag/i.test(lines[0]?.trim())) lines = lines.slice(1);

      const parsedRows = lines.map((line) => {
        const vals = line.split(",");
        const obj: any = {};
        CSV_COLUMNS.forEach((col, i) => (obj[col] = (vals[i] || "").trim()));
        obj.OutrightMargin = "";
        obj.Strategy = "";
        return obj;
      });

      const validRows: any[] = [];
      const errorMessages: string[] = [];

      parsedRows.forEach((row, index) => {
        const errors = validateRow(row);
        if (errors.length > 0) {
          errorMessages.push(`Row ${index + 1}: ${errors.join(" | ")}`);
        } else {
          validRows.push(row);
        }
      });

      if (errorMessages.length > 0) {
        setUploadErrors(errorMessages);
        setErrorDialogOpen(true);
      }

      if (validRows.length > 0) {
        setRows((prev) => [...prev, ...validRows]);
        setUploadedFiles((prev) => [...prev, file.name]);
        toast.success(`${validRows.length} valid row(s) added successfully!`);
      }
    };

    reader.readAsText(file);
  };

  /* ---------- form field renderer ---------- */

  const renderAutocomplete = (field: string) => {
    const options =
      field === "ExchangeCode"
        ? Object.entries(EXCHANGE_CODES).map(([value, label]) => ({
            label: `${value} - ${label}`,
            value,
          }))
        : field === "ContractType"
        ? Object.entries(CONTRACT_TYPES).map(([value, label]) => ({
            label: `${value} - ${label}`,
            value,
          }))
        : field === "Regime"
        ? REGIMES.map((v) => ({ label: v || "-", value: v }))
        : field === "OutrightMargin"
        ? OUTRIGHT_MARGIN_OPTIONS.map((v) => ({ label: v || "-", value: v }))
        : field === "Strategy"
        ? strategyOptions.map((s) => ({
            label: s.label || "-",
            value: s.value,
          }))
        : CUSTOMER_TYPES.map((v) => ({ label: v || "-", value: v }));

    const value = options.find((opt) => opt.value === formData[field]) || null;

    return (
      <Autocomplete
        size="small"
        options={options}
        value={value}
        onChange={(_, newVal) => handleChange(field, newVal?.value || "")}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 180,
              "& .MuiAutocomplete-option": {
                fontSize: "0.7rem",
                minHeight: 26,
                py: 0.3,
              },
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "4px",
            backgroundColor: "transparent",
            "& fieldset": { border: "2px solid #ccc" },
            "&:hover fieldset": { borderColor: "#009db5" },
            "&.Mui-focused fieldset": {
              borderColor: "#009db5",
              borderWidth: "1.5px",
            },
            ...errorBorderSx,
            fontSize: "0.7rem",
            minHeight: 28,
          },
          "& .MuiInputBase-input": { padding: "4px 6px" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={labelFor(field)}
            variant="outlined"
            {...errProps(field)}
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: "0.7rem",
                fontWeight: 700,
                color: formErrors[field] ? "#d32f2f" : "#222",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              },
            }}
          />
        )}
      />
    );
  };

  /* ---------- table columns ---------- */

  const headerMap: Record<string, string> = {
    PositionFlag: "Position/Trade Flag",
    Account: "Portfolio Name",
    ExchangeCode: "Exchange Code",
    ContractCode: "Exchange Contract Code",
    ContractType: "Contract Type",
    ExpiryYYYYMMDD: "Expiry Date",
    Strike: "Strike",
    NetPosition: "Net Position",
    Regime: "Regime",
    CustomerType: "Customer Type",
    OutrightMargin: "Outright Margin",
    Strategy: "Strategy",
  };
  const columns = useMemo(
    () =>
      rows.length > 0
        ? Object.keys(rows[0]).map((key) => ({
            accessorKey: key,
            header: headerMap[key] || key,
            Cell: ({ cell }: any) => (
              <span style={{ fontSize: "0.60rem" }}>{cell.getValue()}</span>
            ),
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );

  // Rows of the finished run-im job
  const imRows = useMemo<any[]>(() => getBufferedRows(imResult), [imResult]);

  const hasStrategy = imRows.some(
    (r) => r?.strategy !== null && r?.strategy !== undefined && String(r.strategy).trim() !== ""
  );

  const imColumns = useMemo(() => {
    return Object.entries(IM_RESULT_HEADERS)
      .filter(([key]) => key !== "strategy" || hasStrategy)
      .map(([key, header]) => ({
      accessorKey: key,
      header,
      size: IM_RESULT_COLUMN_SIZES[key],
      minSize: 70,
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        if (value === null || value === undefined || value === "") return null;
        // buffer factor is a multiplier, not money
        if (key === "buffer_factor") {
          return <span style={{ color: "#334155" }}>{String(value)}</span>;
        }
        if (typeof value === "number") {
          return (
            <span
              style={{
                color: value < 0 ? "#dc2626" : "#16a34a",
                fontWeight: 600,
              }}
            >
              {value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        }
        return (
          <span style={{ color: "#334155" }}>
            {typeof value === "object" ? JSON.stringify(value) : String(value)}
          </span>
        );
      },
    }));
  }, [hasStrategy]);

  const imTotalMargin = useMemo(
    () =>
      typeof imResult?.buffered_total_margin === "number"
        ? imResult.buffered_total_margin
        : undefined,
    [imResult]
  );
  const hasImTotal = imTotalMargin !== undefined;

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              {/* ===== Accordion form ===== */}
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Initial Margin
                  </Accordion.Header>
                  <Accordion.Body>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap", // wrap on smaller screens
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          flex: "0 1 200px",
                          height: 32,
                          border: "1.4px dashed rgba(150,160,180,0.6)",
                          borderRadius: "8px",
                          background: "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          transition: "all 0.25s ease-in-out",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            background: "linear-gradient(160deg, #dce9ff 0%, #f0f2f5 100%)",
                          },
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files[0];
                          if (
                            file &&
                            (file.name.endsWith(".csv") || file.name.endsWith(".txt"))
                          ) {
                            handleUpload({ target: { files: [file] } } as any);
                          } else {
                            toast.error("Please drop a valid .csv or .txt file.");
                          }
                        }}
                      >
                        <CloudUploadIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        <Typography
                          variant="body2"
                          sx={{ color: "#2d3b50", fontWeight: 600, fontSize: "0.70rem" }}
                        >
                          Upload CSV
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          component="label"
                          sx={{
                            textTransform: "none",
                            fontSize: "0.55rem",
                            borderRadius: "5px",
                            px: 1,
                            py: 0.2,
                            minHeight: 24,
                            marginLeft: "20px",
                            background: "#009db5",
                            "&:hover": {
                              background: "#009db5",
                            },
                          }}
                        >
                          Browse
                          <input
                            hidden
                            accept=".csv,.txt"
                            type="file"
                            onChange={handleUpload}
                          />
                        </Button>
                      </Box>

                      {/* Show uploaded file names only if at least one file succeeded */}
                      {uploadedFiles.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            maxHeight: 90,
                            overflowY: "auto",
                            p: 0.5,
                            border: "1px solid rgba(150,160,180,0.3)",
                            borderRadius: "6px",
                            background: "#f9fafb",
                          }}
                        >
                          {uploadedFiles.map((name, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                background: "#fff",
                                px: 1,
                                py: 0.2,
                                borderRadius: "6px",
                                border: "1px solid rgba(150,160,180,0.2)",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#4b5563",
                                  fontStyle: "italic",
                                  maxWidth: 140,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setUploadedFiles((prev) =>
                                    prev.filter((_, idx) => idx !== i)
                                  )
                                }
                                sx={{
                                  p: 0,
                                  color: "#dc2626",
                                  "&:hover": { color: "#b91c1c" },
                                }}
                              >
                                <ClearIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Filter Toggle Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterAltIcon fontSize="inherit" />}
                        sx={{
                          flex: "0 1 80px",
                          minWidth: 80,
                          height: 32,
                          textTransform: "none",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          borderRadius: "8px",
                          px: 1,
                          borderColor: "rgba(150,160,180,0.6)",
                          color: "#2d3b50",
                          background: "linear-gradient(160deg, #f9fafb 0%, #f1f3f6 100%)",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            color: "#3b82f6",
                            background: "linear-gradient(160deg, #e8f1ff 0%, #f4f5f7 100%)",
                          },
                        }}
                        onClick={() => setShowFilter(!showFilter)}
                      >
                        Filter
                      </Button>

                      {/* Filter Fields */}
                      {showFilter &&
                        filtersConfig.map((f) => {
                          const filterValue = filters[f.key];
                          const hasValue = Array.isArray(filterValue)
                            ? filterValue.length > 0
                            : !!filterValue;
                          return (
                            <React.Fragment key={f.key}>
                              <TextField
                                select
                                size="small"
                                label={f.label}
                                value={filterValue}
                                onChange={(e) => {
                                  const newValue: unknown = e.target.value;
                                  setFilters({
                                    ...filters,
                                    [f.key]:
                                      f.multiple && typeof newValue === "string"
                                        ? newValue.split(",").filter(Boolean)
                                        : newValue,
                                  });
                                }}
                                SelectProps={
                                  f.multiple
                                    ? {
                                      multiple: true,
                                      renderValue: (selected) =>
                                        (selected as string[])
                                          .map(
                                            (v) =>
                                              f.options.find((o) => o.value === v)
                                                ?.label ?? v
                                          )
                                          .join(", "),
                                    }
                                    : undefined
                                }
                                InputProps={{
                                  endAdornment: hasValue ? (
                                    <InputAdornment position="start">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setFilters({
                                            ...filters,
                                            [f.key]: f.multiple ? [] : "",
                                          })
                                        }
                                        sx={{
                                          p: 0.6,
                                          "& .MuiSvgIcon-root": { fontSize: "0.8rem" },
                                        }}
                                      >
                                        <ClearIcon fontSize="inherit" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : null,
                                }}
                                sx={{
                                  flex: "0 1 140px",
                                  minWidth: 120,
                                  maxWidth: 160,
                                  height: 34,
                                  "& .MuiInputBase-root": {
                                    fontSize: "0.7rem",
                                    height: 34,
                                    background:
                                      "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                                    borderRadius: "8px",
                                    overflow: "visible",
                                  },
                                  "& .MuiSelect-select": f.multiple
                                    ? {
                                      // keep long multi-selections inside the field
                                      display: "block",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontSize: "0.7rem",
                                    }
                                    : {
                                      display: "flex",
                                      alignItems: "center",
                                      whiteSpace: "nowrap",
                                      overflow: "visible",
                                      textOverflow: "unset",
                                      fontSize: "0.7rem",
                                    },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.7rem",
                                    top: -2,
                                    color: "#3b4a66",
                                    marginLeft: -1,
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(150,160,180,0.4)",
                                  },
                                }}
                              >
                                {f.options.map((opt) => (
                                  <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    {f.multiple && (
                                      <Checkbox
                                        size="small"
                                        checked={(filterValue as string[]).includes(opt.value)}
                                        sx={{ p: 0, mr: 0.8 }}
                                      />
                                    )}
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              {f.key === "exchange" && (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <DatePicker
                                    label="Trades till"
                                    format={TILL_DATE_FORMAT}
                                    value={tillDate}
                                    onChange={(newValue) => setTillDate(newValue)}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        variant: "outlined",
                                        sx: {
                                          flex: "0 1 150px",
                                          minWidth: 130,
                                          maxWidth: 160,
                                          "& .MuiInputBase-root": {
                                            fontSize: "0.7rem",
                                            height: 34,
                                            background:
                                              "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                                            borderRadius: "8px",
                                          },
                                          "& .MuiInputLabel-root": {
                                            fontSize: "0.7rem",
                                            color: "#3b4a66",
                                          },
                                          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                                            top: -2,
                                          },
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgba(150,160,180,0.4)",
                                          },
                                          "& input": { padding: "6px 8px" },
                                          "& .MuiSvgIcon-root": { fontSize: "1rem" },
                                        },
                                      },
                                      field: { clearable: true },
                                    }}
                                  />
                                </LocalizationProvider>
                              )}
                              {f.key === "exchange" && (
                                <TextField
                                  select
                                  size="small"
                                  label="Till date"
                                  value={includeTillDate ? "include" : "exclude"}
                                  onChange={(e) => setIncludeTillDate(e.target.value === "include")}
                                  sx={{
                                    flex: "0 1 190px",
                                    minWidth: 170,
                                    maxWidth: 200,
                                    height: 34,
                                    "& .MuiInputBase-root": {
                                      fontSize: "0.7rem",
                                      height: 34,
                                      background: "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                                      borderRadius: "8px",
                                    },
                                    "& .MuiSelect-select": {
                                      display: "flex",
                                      alignItems: "center",
                                      whiteSpace: "nowrap",
                                      fontSize: "0.7rem",
                                    },
                                    "& .MuiInputLabel-root": {
                                      fontSize: "0.7rem",
                                      top: -2,
                                      color: "#3b4a66",
                                      marginLeft: -1,
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgba(150,160,180,0.4)",
                                    },
                                  }}
                                >
                                  <MenuItem value="include" sx={{ fontSize: "0.7rem" }}>
                                    Include selected date
                                  </MenuItem>
                                  <MenuItem value="exclude" sx={{ fontSize: "0.7rem" }}>
                                    Up to previous business day
                                  </MenuItem>
                                </TextField>
                              )}
                            </React.Fragment>
                          );
                        })}

                      {/* Clear All Icon */}
                      {showFilter && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFilters(makeEmptyFilters());
                            setTillDate(null);
                            setIncludeTillDate(true);
                            resetTable();
                          }}
                          sx={{
                            flex: "0 0 32px",
                            height: 32,
                            minWidth: 32,
                            color: "#585bcc",
                            backgroundColor: "rgba(59,130,246,0.1)",
                            "&:hover": {
                              backgroundColor: "rgba(59,130,246,0.2)",
                            },
                          }}
                          title="Clear All Filters"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}

                      {/* Fetch Data Button */}
                      {showFilter && (
                        <Button
                          variant="contained"
                          onClick={handleFilterApply}
                          sx={{
                            flexShrink: 0,
                            minWidth: 100,
                            height: 32,
                            textTransform: "none",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            px: 2.5,
                            py: 0.5,
                            backgroundColor: "#009db5",
                            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#008699",
                              boxShadow: "0px 3px 6px rgba(0,0,0,0.15)",
                            },
                            "&:active": {
                              backgroundColor: "#007b8a",
                              boxShadow: "inset 0px 2px 4px rgba(0,0,0,0.2)",
                            },
                          }}
                        >
                          Fetch Data
                        </Button>
                      )}
                    </Box>
                    {Array.isArray(editIndex) && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          fontWeight: 700,
                          color: "#b33a00",
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "0.75rem",
                        }}
                      >
                        Bulk Edit Mode: Changes will apply to {editIndex.length}{" "}
                        selected rows.
                      </Typography>
                    )}
                    <Grid
                      container
                      spacing={1.5}
                      rowSpacing={1.5}
                      columnSpacing={1.5}
                    >
                      {Object.keys(makeEmptyRow()).map((field) => (
                        <Grid item xs={12} sm={4} md={2.5} lg={2} key={field}>
                          {field === "PositionFlag" ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Position/Trade Flag"
                              value="P"
                              disabled
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                                sx: {
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: "#222",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "4px",
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "2px solid #ccc" },
                                  fontSize: "0.7rem",
                                  minHeight: "34px",
                                },
                                "& .MuiInputBase-input": { padding: "4px 6px" },
                              }}
                            />
                          ) : field === "ExpiryYYYYMMDD" ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={labelFor(field)}
                                  views={isDayExpiry ? ["year", "month", "day"] : ["year", "month"]}
                                  format={isDayExpiry ? "YYYYMMDD" : "YYYYMM"}
                                  value={
                                    expiryStr
                                      ? isDayExpiry && !expiryStr.endsWith("00")
                                        ? dayjs(expiryStr, "YYYYMMDD")
                                        : dayjs(expiryStr.slice(0, 6), "YYYYMM") // keep only YYYYMM
                                      : null
                                  }
                                  onChange={(newValue) => {
                                    if (!newValue) {
                                      handleChange(field, "");
                                      return;
                                    }

                                    if (isDayExpiry) {
                                      // Daily → YYYYMMDD
                                      handleChange(field, newValue.format("YYYYMMDD"));
                                    } else {
                                      // Monthly → YYYYMM00 (no shift)
                                      handleChange(field, `${newValue.format("YYYYMM")}00`);
                                    }
                                  }}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      fullWidth: true,
                                      variant: "outlined",
                                      error: !!formErrors[field],
                                      InputLabelProps: {
                                        shrink: true,
                                        sx: {
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          color: formErrors[field] ? "#d32f2f" : "#222",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.4px",
                                        },
                                      },
                                      InputProps: {
                                        startAdornment: (
                                          <InputAdornment position="start" sx={{ mr: 0.5, ml: 0 }}>
                                            <Tooltip title="Pick a specific day. Unticked = month only (DD=00).">
                                              <Box
                                                component="label"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  fontSize: "0.65rem",
                                                  fontWeight: 700,
                                                  color: "#222",
                                                  cursor:
                                                    formData.ContractType === "D" ? "default" : "pointer",
                                                  whiteSpace: "nowrap",
                                                  pr: 0.75,
                                                  borderRight: "1px solid #ccc",
                                                }}
                                              >
                                                <Checkbox
                                                  size="small"
                                                  checked={isDayExpiry}
                                                  disabled={formData.ContractType === "D"}
                                                  onChange={(e) => handleExpiryDayToggle(e.target.checked)}
                                                  sx={{ p: 0.25 }}
                                                />
                                                Day
                                              </Box>
                                            </Tooltip>
                                          </InputAdornment>
                                        ),
                                      },
                                      sx: {
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "4px",
                                          backgroundColor: "transparent",
                                          "& fieldset": {
                                            border: `2px solid ${formErrors[field] ? "#d32f2f" : "#ccc"}`,
                                          },
                                          "&:hover fieldset": {
                                            borderColor: formErrors[field] ? "#d32f2f" : "#009db5",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: formErrors[field] ? "#d32f2f" : "#009db5",
                                            borderWidth: "1.5px",
                                          },
                                          fontSize: "0.7rem",
                                          minHeight: "34px",
                                          pl: 0.5,
                                        },
                                        "& input": {
                                          fontFamily: "monospace",
                                          letterSpacing: "0.4px",
                                        },
                                        "& .MuiInputBase-input": {
                                          padding: "8px 8px",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                          ) : [
                            "ExchangeCode",
                            "ContractType",
                            "Regime",
                            "CustomerType",
                            "OutrightMargin",
                            "Strategy",
                          ].includes(field) ? (
                            renderAutocomplete(field)
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label={labelFor(field)}
                              value={formData[field]}
                              onChange={(e) => handleChange(field, e.target.value)}
                              variant="outlined"
                              {...errProps(field)}
                              InputLabelProps={{
                                shrink: true,
                                sx: {
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: formErrors[field] ? "#d32f2f" : "#222",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "4px",
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "2px solid #ccc" },
                                  "&:hover fieldset": { borderColor: "#009db5" },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#009db5",
                                    borderWidth: "1.5px",
                                  },
                                  ...errorBorderSx,
                                  fontSize: "0.7rem",
                                  minHeight: "34px",
                                },
                                "& .MuiInputBase-input": { padding: "4px 6px" },
                              }}
                            />
                          )}
                        </Grid>
                      ))}

                      {/* Save / Update Button */}
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        md={1}
                        lg={1}
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                        }}
                        gap={1}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddOrUpdate}
                          sx={{
                            height: { xs: "36px", sm: "32px" },
                            flex: { xs: "1 1 auto", sm: "0 0 auto" },
                            minWidth: { xs: "100%", sm: "100px" },
                            maxWidth: { sm: "150px" },
                            backgroundColor: "#009db5",
                            textTransform: "none",
                            fontSize: "0.75rem",
                            "&:hover": {
                              backgroundColor: "#008c9e",
                            },
                          }}
                        >
                          {editIndex !== null ? "Update" : "Add"}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleClearForm}
                          disabled={!hasFormChanges}
                          sx={{
                            height: { xs: "36px", sm: "32px" },
                            flex: { xs: "1 1 auto", sm: "0 0 auto" },
                            minWidth: { xs: "100%", sm: "100px" },
                            fontSize: "0.75rem",
                            textTransform: "none",
                            maxWidth: { sm: "150px" },
                            borderColor: "#f44336",
                            color: "white",
                            "&:hover": {
                              borderColor: "#d32f2f",
                              backgroundColor: "#d32f2f",
                            },
                          }}
                        >
                          Clear
                        </Button>
                      </Grid>
                    </Grid>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* ===== IM RESULT (above positions table) ===== */}
              <div ref={imResultRef} />
              {(hasImTotal || imRows.length > 0) && (
                <Box sx={{ mt: 3, mb: 3 }}>
                  {/* Section heading */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 22,
                        borderRadius: 2,
                        background: "linear-gradient(180deg, #22d3ee 0%, #6366f1 100%)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        letterSpacing: 1.6,
                        textTransform: "uppercase",
                        color: "#0f172a",
                      }}
                    >
                      Initial Margin Result
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        background:
                          "linear-gradient(90deg, rgba(148,163,184,0.6) 0%, transparent 100%)",
                      }}
                    />
                  </Box>

                  {/* Compact summary card */}
                  <Box
                    onClick={() => setShowImTable((prev) => !prev)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderLeft: "3px solid #1976d2",
                      "&:hover": { backgroundColor: "#f8f9fa" },
                    }}
                  >
                    {/* Total */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase" }}>
                        Total Initial Margin
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.95rem",
                          fontWeight: 800,
                          lineHeight: 1.2,
                          color: (imTotalMargin ?? 0) < 0 ? "#d32f2f" : "#2e7d32",
                        }}
                      >
                        {hasImTotal
                          ? Number(imTotalMargin).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          : "—"}
                      </Typography>
                      {imReused && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            color: "#b26a00",
                            backgroundColor: "#fff4e5",
                            borderRadius: "8px",
                            px: 0.75,
                          }}
                        >
                          Reused from earlier run
                        </Typography>
                      )}
                    </Box>

                    {/* Stats + actions */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                        Rows: <b style={{ color: "#000" }}>{imRows.length}</b>
                      </Typography>

                      {lastDate && lastDate.isValid() && (
                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Date: <b style={{ color: "#000" }}>{lastDate.format("DD MMM YYYY")}</b>
                        </Typography>
                      )}

                      {lastImRequestRef.current && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCalculating}
                          startIcon={
                            isCalculating ? (
                              <CircularProgress size={10} thickness={5} />
                            ) : (
                              <RefreshIcon sx={{ fontSize: "0.8rem !important" }} />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setRerunConfirmOpen(true);
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            py: 0,
                            px: 1,
                            minHeight: 22,
                            borderRadius: "4px",
                          }}
                        >
                          {isCalculating ? "Recalculating…" : "Rerun"}
                        </Button>
                      )}

                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "primary.main" }}>
                        {showImTable ? "Hide Details ▴" : "View Details ▾"}
                      </Typography>
                    </Box>
                  </Box>

                  {showImTable && imRows.length > 0 && (
                    <div className="table-ad__wrapperCard" style={{ marginBottom: 32 }}>
                      <MaterialReactTable
                        columns={imColumns}
                        data={imRows}
                        enableColumnActions={false}
                        enableColumnFilters={false}
                        enableSorting
                        enablePagination
                        enableRowSelection={false}
                        enableGlobalFilter={false}
                        enableStickyHeader
                        initialState={{ pagination: { pageIndex: 0, pageSize: 100 } }}
                        renderToolbarInternalActions={({ table }) => (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <MRT_ToggleDensePaddingButton table={table} />
                            <TableColumnVisibilityMenu table={table} />
                            <MRT_ToggleFullScreenButton table={table} />
                          </Box>
                        )}
                        muiTopToolbarProps={{
                          sx: { backgroundColor: "#fff", padding: "8px" },
                          className: "top-toolbar-wrapper__new",
                        }}
                        muiBottomToolbarProps={{
                          sx: { backgroundColor: "#fff", padding: "8px" },
                          className: "bottom-toolbar-wrapper__new",
                        }}
                        muiTableHeadCellProps={{
                          sx: {
                            border: "1px solid #e0e0e087",
                            fontStyle: "normal",
                            fontWeight: "normal",
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                          },
                        }}
                        muiTableBodyCellProps={{
                          sx: {
                            fontSize: "0.75rem",
                            padding: "4px 6px",
                            lineHeight: 1,
                            minHeight: 28,
                            backgroundColor: "transparent !important",
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                          },
                        }}
                        muiTableContainerProps={tableContainerProps}
                      />
                    </div>
                  )}
                </Box>
              )}

              {/* ===== MAIN TABLE (Books structure) ===== */}
              <div className="table-ad__wrapper">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable
                    columns={[
                      ...columns,
                      {
                        id: "actions",
                        header: showActionsHeader ? "Actions" : "",
                        size: 80,
                        muiTableHeadCellProps: {
                          sx: {
                            width: 80,
                            maxWidth: 80,
                            minWidth: 80,
                            padding: "4px",
                            borderRight: "1px solid #ddd",
                          },
                        },
                        muiTableBodyCellProps: {
                          sx: {
                            width: 80,
                            maxWidth: 80,
                            minWidth: 80,
                            padding: "4px",
                            borderRight: "1px solid #ddd",
                          },
                        },
                        Header: () => (showActionsHeader ? "Actions" : null),
                        Cell: ({ row }) => (
                          <Box display="flex" justifyContent="center" gap={0.3}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(row.index)}
                                sx={{
                                  p: 0.3,
                                  "& svg": { fontSize: "0.9rem" },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(row.index)}
                                sx={{
                                  p: 0.3,
                                  "& svg": { fontSize: "0.9rem" },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ),
                      },
                    ]}
                    data={rows}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableSorting
                    enablePagination
                    enableStickyHeader
                    enableRowSelection
                    selectAllMode="all"
                    onRowSelectionChange={setRowSelection}
                    enableGlobalFilter={false}
                    state={{ rowSelection, globalFilter }}
                    onGlobalFilterChange={setGlobalFilter}
                    initialState={{
                      pagination: {
                        pageIndex: 0,
                        pageSize: 100, // Default rows per page
                      },
                    }}
                    renderToolbarInternalActions={({ table }) => (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <MRT_ToggleGlobalFilterButton table={table} />
                        <MRT_ToggleFiltersButton table={table} />
                        <MRT_ToggleDensePaddingButton table={table} />
                        <TableColumnVisibilityMenu table={table} />
                        <MRT_ToggleFullScreenButton table={table} />
                      </Box>
                    )}
                    renderTopToolbarCustomActions={({ table }) => (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        sx={{ py: 0.3, px: 1 }}
                      >
                        {/* LEFT — action buttons */}
                        <Box display="flex" alignItems="center" gap={1}>
                          {table.getSelectedRowModel().rows.length > 0 && (
                            <Button
                              variant="text"
                              startIcon={<DeleteIcon />}
                              onClick={() =>
                                handleBulkDelete(table.getSelectedRowModel().rows)
                              }
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                pl: 0,

                                color: "red !important",
                                "& .MuiSvgIcon-root": {
                                  color: "red !important",
                                },

                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "darkred !important",
                                  "& .MuiSvgIcon-root": {
                                    color: "darkred !important",
                                  },
                                },
                              }}
                            >
                              Delete Selected (
                              {table.getSelectedRowModel().rows.length})
                            </Button>
                          )}
                          {table.getSelectedRowModel().rows.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Tooltip title="Edit Selected">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleBulkEdit(table.getSelectedRowModel().rows)
                                  }
                                  sx={{ ml: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Button
                                variant="text"
                                onClick={() =>
                                  handleBulkEdit(table.getSelectedRowModel().rows)
                                }
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "inherit",
                                  padding: 0,
                                  minWidth: "unset",
                                }}
                              >
                                Edit Selected (
                                {table.getSelectedRowModel().rows.length})
                              </Button>
                            </Box>
                          )}

                          {rows.length > 0 && (
                            <Tooltip title="Validate All Rows">
                              <IconButton size="small" onClick={handleValidateAll}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Show icon + text when rows are selected */}
                          {table.getSelectedRowModel().rows.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Tooltip title="Calculate IM for Selected">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={openCalculateDialog}
                                    disabled={isCalculating}
                                    sx={{ ml: 0.5 }}
                                  >
                                    {isCalculating ? (
                                      <CircularProgress size={18} thickness={5} />
                                    ) : (
                                      <CalculateIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Button
                                variant="text"
                                onClick={openCalculateDialog}
                                disabled={isCalculating}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "inherit",
                                  padding: 0,
                                  minWidth: "unset",
                                }}
                              >
                                Calculate Selected (
                                {table.getSelectedRowModel().rows.length})
                              </Button>
                            </Box>
                          )}

                          {/* Show only icon when NO rows selected */}
                          {table.getSelectedRowModel().rows.length === 0 && (
                            <Tooltip title="Calculate IM">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={openCalculateDialog}
                                  disabled={rows.length === 0 || isCalculating}
                                >
                                  {isCalculating ? (
                                    <CircularProgress size={18} thickness={5} />
                                  ) : (
                                    <CalculateIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Button
                            variant="text"
                            onClick={() => {
                              setRows([]);
                              setImResult(null);
                              setShowActionsHeader(false);
                            }}
                            startIcon={<RefreshIcon />}
                            sx={{
                              textTransform: "none",
                              borderRadius: "8px",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              color: "#0277bd",
                              px: 2,
                              "&:hover": { backgroundColor: "rgba(2,119,189,0.1)" },
                            }}
                          >
                            Clear
                          </Button>
                        </Box>

                        {/* RIGHT — Search + Download */}
                        <Box display="flex" alignItems="center" gap={1}>
                          <TextField
                            size="small"
                            placeholder="Search..."
                            variant="outlined"
                            value={globalFilter || ""}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 32,
                                fontSize: "0.75rem",
                                background: "#f9fafb",
                                borderRadius: "8px",
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "4px 8px",
                              },
                              width: 160,
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <SearchIcon sx={{ fontSize: 16, color: "#888" }} />
                                </InputAdornment>
                              ),
                              "aria-label": "table search",
                            }}
                          />

                          {rows.length > 0 && (
                            <Tooltip title="Download">
                              <IconButton size="small" onClick={handleDownload}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    )}
                    muiTopToolbarProps={{
                      sx: {
                        backgroundColor: "#fff",
                        padding: "8px",
                        "& .MuiBox-root.css-wsew38": {
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        },
                      },
                      className: "top-toolbar-wrapper__new",
                    }}
                    muiBottomToolbarProps={{
                      sx: {
                        backgroundColor: "#fff",
                        padding: "8px",
                      },
                      className: "bottom-toolbar-wrapper__new",
                    }}
                    muiTableHeadCellProps={{
                      sx: {
                        border: "1px solid #e0e0e087",
                        fontStyle: "normal",
                        fontWeight: "normal",
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                      },
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        fontSize: "0.75rem",
                        padding: "4px 6px",
                        lineHeight: 1,
                        minHeight: 28,
                        backgroundColor: "transparent !important",
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                        "& .MuiCheckbox-root": {
                          paddingLeft: "15px !important",
                        },
                      },
                    }}
                    muiTableContainerProps={tableContainerProps}
                  />
                </div>
              </div>

              {/* Notes */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                Notes: Fields marked * are required. Strike must be numeric (required for
                options). Use DD=00 for monthly futures. Regime and Customer Type optional.
              </Typography>
            </div>
          </div>
        </Row>
      </Container>

      {/* ===== Dialogs: outside Container, directly inside page-body ===== */}

      {/* Rerun confirm */}
      <Dialog
        open={rerunConfirmOpen}
        onClose={() => setRerunConfirmOpen(false)}
        PaperProps={{ sx: { width: 360, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
          Rerun Initial Margin
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.8rem" }}>
            This will recalculate margin and can take a few minutes. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setRerunConfirmOpen(false)}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRerunIM}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "#009db5",
              "&:hover": { backgroundColor: "#008699" },
            }}
          >
            Rerun
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={confirmOpen}
        onClose={cancelDelete}
        PaperProps={{
          sx: {
            width: 320,
            p: 1,
            borderRadius: 2,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            px: 2,
            pt: 2.5,
            pb: 1,
          }}
        >
          <Box display="flex" justifyContent="center" mb={1}>
            <WarningAmberRoundedIcon sx={{ color: "#f59e0b", fontSize: 34 }} />
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 0.5, fontSize: "0.9rem" }}
          >
            Confirm Delete
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            {deleteIndex !== null
              ? "Delete this record?"
              : "Delete selected records?"}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Button
            onClick={cancelDelete}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload validation errors */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#b71c1c",
            bgcolor: "#ffebee",
            borderBottom: "1px solid #f5c2c2",
          }}
        >
          Validation Errors
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            p: 1.5,
            bgcolor: "#fffdfd",
          }}
        >
          {uploadErrors.map((err, i) => (
            <Box
              key={i}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: "6px",
                bgcolor: "#fff3f3",
                border: "1px solid #ffcdd2",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#b71c1c", fontSize: "0.8rem", lineHeight: 1.3 }}
              >
                {err}
              </Typography>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ p: 1 }}>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              bgcolor: "#b71c1c",
              "&:hover": { bgcolor: "#a31212" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Business date popup for Calculate IM */}
      <Dialog
        open={dateDialogOpen}
        onClose={() => {
          setDateDialogOpen(false);
          setDateError("");
        }}
        PaperProps={{ sx: { width: 340, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
          Select IM File Date
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="IM File Date *"
              format={LAST_DATE_FORMAT}
              value={lastDate}
              onChange={(newValue) => {
                setLastDate(newValue);
                setDateError("");
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: !!dateError,
                  helperText: dateError,
                  sx: { mt: 1 },
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDateDialogOpen(false);
              setDateError("");
            }}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCalculateIM}
            variant="contained"
            size="small"
            disabled={isCalculating}
            sx={{ textTransform: "none", backgroundColor: "#009db5" }}
          >
            Calculate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InitialMargin;